/**
 * Chart component for assistant-ui data parts (type "data-chart").
 * Renders bar, line, pie, or radar charts via Recharts.
 */

import { Box, Text } from "@chakra-ui/react";
import React, { useMemo } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
  YAxis,
} from "recharts";

export type ChartSeries = {
  name: string;
  values?: number[];
  data?: number[];
  value?: number[];
};

export type ChartSpec = {
  title?: string;
  type?: string;
  labels?: string[];
  values?: number[];
  series?: ChartSeries[];
};

const CHART_COLORS = [
  "#805AD5",
  "#3182CE",
  "#38A169",
  "#DD6B20",
  "#E53E3E",
  "#D69E2E",
  "#00B5D8",
  "#ED64A6",
];
const REPORT_CHART_COLORS = [
  "#7E5BEF",
  "#9B8AFB",
  "#B7A8FF",
  "#D4CBFF",
  "#4D37DE",
  "#6556D9",
];
const DEFAULT_PRIMARY_CHART_COLOR = "#805AD5";
const REPORT_PRIMARY_CHART_COLOR = "#7E5BEF";
const DEFAULT_CHART_AXIS_COLOR = "#4A5568";
const REPORT_CHART_AXIS_COLOR = "#808B97";
const DEFAULT_CHART_GRID_COLOR = "#E2E8F0";
const REPORT_CHART_GRID_COLOR = "#E5E7EA";
const DEFAULT_BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];
const REPORT_BAR_RADIUS: [number, number, number, number] = [0, 0, 0, 0];
const DEFAULT_CHART_MARGIN = { top: 8, right: 8, left: 8, bottom: 8 };
const REPORT_CHART_MARGIN = { top: 8, right: 8, left: 0, bottom: 8 };

type InsightsChartPartProps = {
  data: ChartSpec;
  variant?: "default" | "insightsReport";
};

export const InsightsChartPart: React.FC<InsightsChartPartProps> = ({
  data: spec,
  variant = "default",
}) => {
  const chartType = spec.type ?? "bar";
  const isInsightsReport = variant === "insightsReport";
  const seriesNames = useMemo(
    () => spec.series?.map((s, i) => s.name ?? `Series ${i + 1}`) ?? [],
    [spec.series]
  );
  const isMultiSeries = seriesNames.length > 0;

  const data = useMemo(() => {
    const labels = spec.labels ?? [];
    const truncate = (s: string): string =>
      s.length > 20 ? `${s.slice(0, 20)}…` : s;
    if (isMultiSeries && spec.series) {
      return labels.map((label, i) => {
        const row: Record<string, string | number> = {
          name: truncate(label),
        };
        spec.series?.forEach((s, si) => {
          const key = s.name ?? `Series ${si + 1}`;
          const vals = s.values ?? s.data ?? s.value ?? [];
          row[key] = vals[i] ?? 0;
        });
        return row;
      });
    }
    return labels.map((label, i) => ({
      name: truncate(label),
      value: spec.values?.[i] ?? 0,
    }));
  }, [spec.labels, spec.values, spec.series, isMultiSeries]);

  if (!data.length) return null;

  const chartColor = isInsightsReport
    ? REPORT_PRIMARY_CHART_COLOR
    : DEFAULT_PRIMARY_CHART_COLOR;
  const secondaryChartColors = isInsightsReport
    ? REPORT_CHART_COLORS
    : CHART_COLORS;
  const axisColor = isInsightsReport
    ? REPORT_CHART_AXIS_COLOR
    : DEFAULT_CHART_AXIS_COLOR;
  const gridColor = isInsightsReport
    ? REPORT_CHART_GRID_COLOR
    : DEFAULT_CHART_GRID_COLOR;
  const barRadius = isInsightsReport ? REPORT_BAR_RADIUS : DEFAULT_BAR_RADIUS;
  const chartMargin = isInsightsReport
    ? REPORT_CHART_MARGIN
    : DEFAULT_CHART_MARGIN;
  const titleProps = isInsightsReport
    ? {
        fontSize: "16px",
        fontWeight: "600",
        lineHeight: "24px",
        color: "gray.800",
        mb: 4,
      }
    : {
        fontSize: "sm",
        fontWeight: "medium",
        mb: 2,
      };

  const renderChart = (): React.ReactNode => {
    switch (chartType) {
      case "line":
        return (
          <LineChart data={data} margin={chartMargin}>
            <CartesianGrid
              stroke={gridColor}
              strokeDasharray={isInsightsReport ? undefined : "3 3"}
              vertical={!isInsightsReport}
            />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: axisColor }} />
            <YAxis tick={{ fontSize: 11, fill: axisColor }} />
            <RechartsTooltip />
            {isMultiSeries ? (
              seriesNames.map((name, i) => (
                <Line
                  key={name}
                  type="monotone"
                  dataKey={name}
                  stroke={secondaryChartColors[i % secondaryChartColors.length]}
                  strokeWidth={2}
                  dot={{ r: 4 }}
                />
              ))
            ) : (
              <Line
                type="monotone"
                dataKey="value"
                stroke={chartColor}
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            )}
            {isMultiSeries && <Legend />}
          </LineChart>
        );
      case "pie":
        return (
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, percent }: { name: string; percent: number }) =>
                `${name} ${(percent * 100).toFixed(0)}%`
              }
            >
              {data.map((entry, idx) => (
                <Cell
                  // eslint-disable-next-line react/no-array-index-key
                  key={`cell-${idx}`}
                  fill={secondaryChartColors[idx % secondaryChartColors.length]}
                />
              ))}
            </Pie>
            <RechartsTooltip />
            <Legend />
          </PieChart>
        );
      case "radar":
        return (
          <RadarChart cx="50%" cy="50%" outerRadius="70%" data={data}>
            <PolarGrid />
            <PolarAngleAxis dataKey="name" tick={{ fontSize: 11 }} />
            <PolarRadiusAxis tick={{ fontSize: 10 }} />
            {isMultiSeries ? (
              seriesNames.map((name, i) => (
                <Radar
                  key={name}
                  dataKey={name}
                  stroke={secondaryChartColors[i % secondaryChartColors.length]}
                  fill={secondaryChartColors[i % secondaryChartColors.length]}
                  fillOpacity={0.2}
                />
              ))
            ) : (
              <Radar
                dataKey="value"
                stroke={chartColor}
                fill={chartColor}
                fillOpacity={0.3}
              />
            )}
            <RechartsTooltip />
            {isMultiSeries && <Legend />}
          </RadarChart>
        );
      case "stacked_bar":
        return (
          <BarChart data={data} margin={chartMargin}>
            <CartesianGrid
              stroke={gridColor}
              strokeDasharray={isInsightsReport ? undefined : "3 3"}
              vertical={!isInsightsReport}
            />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: axisColor }} />
            <YAxis tick={{ fontSize: 11, fill: axisColor }} />
            <RechartsTooltip />
            {isMultiSeries ? (
              seriesNames.map((name, i) => (
                <Bar
                  key={name}
                  dataKey={name}
                  stackId="stack"
                  fill={secondaryChartColors[i % secondaryChartColors.length]}
                  radius={barRadius}
                />
              ))
            ) : (
              <Bar dataKey="value" fill={chartColor} radius={barRadius} />
            )}
            {isMultiSeries && <Legend />}
          </BarChart>
        );
      default:
        return (
          <BarChart data={data} margin={chartMargin}>
            <CartesianGrid
              stroke={gridColor}
              strokeDasharray={isInsightsReport ? undefined : "3 3"}
              vertical={!isInsightsReport}
            />
            <XAxis dataKey="name" tick={{ fontSize: 11, fill: axisColor }} />
            <YAxis tick={{ fontSize: 11, fill: axisColor }} />
            <RechartsTooltip />
            {isMultiSeries ? (
              seriesNames.map((name, i) => (
                <Bar
                  key={name}
                  dataKey={name}
                  fill={secondaryChartColors[i % secondaryChartColors.length]}
                  radius={barRadius}
                />
              ))
            ) : (
              <Bar dataKey="value" fill={chartColor} radius={barRadius} />
            )}
            {isMultiSeries && <Legend />}
          </BarChart>
        );
    }
  };

  return (
    <Box mt={isInsightsReport ? 0 : 3} width="100%" minH="200px">
      {spec.title && <Text {...titleProps}>{spec.title}</Text>}
      <ResponsiveContainer width="100%" height={260}>
        {renderChart() as React.ReactElement}
      </ResponsiveContainer>
    </Box>
  );
};
